import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { Helmet } from "react-helmet-async";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbNavProps {
  items: BreadcrumbItem[];
  language?: 'es' | 'en';
}

export const BreadcrumbNav = ({ items, language = 'es' }: BreadcrumbNavProps) => {
  const homeText = language === 'es' ? 'Inicio' : 'Home';
  
  const homeUrl = language === 'en' ? '/en' : '/';

  const allItems = [
    { name: homeText, url: homeUrl },
    ...items
  ];

  // Schema.org structured data for breadcrumbs
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": allItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://luisroc.com${item.url}`
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav 
        aria-label={language === 'es' ? 'Ruta de navegación' : 'Breadcrumb navigation'}
        className="py-4 text-sm"
      >
        <ol 
          className="flex flex-wrap items-center gap-2 text-muted-foreground"
          itemScope 
          itemType="https://schema.org/BreadcrumbList"
        >
          {allItems.map((item, index) => {
            const isLast = index === allItems.length - 1;
            
            return (
              <li 
                key={index} 
                className="flex items-center gap-2"
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                {index === 0 ? (
                  <Link 
                    to={item.url}
                    className="hover:text-foreground transition-colors flex items-center gap-1"
                    itemProp="item"
                  >
                    <Home className="h-4 w-4" />
                    <span itemProp="name">{item.name}</span>
                  </Link>
                ) : (
                  <>
                    <ChevronRight className="h-4 w-4" />
                    {isLast ? (
                      <span 
                        className="text-foreground font-medium" 
                        itemProp="name"
                        aria-current="page"
                      >
                        {item.name}
                      </span>
                    ) : (
                      <Link 
                        to={item.url}
                        className="hover:text-foreground transition-colors"
                        itemProp="item"
                      >
                        <span itemProp="name">{item.name}</span>
                      </Link>
                    )}
                  </>
                )}
                <meta itemProp="position" content={String(index + 1)} />
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};
